import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as registrationActions from "@rentiohq/shared-frontend/dist/redux/registration/registration.actions";
import * as registrationSelectors from "@rentiohq/shared-frontend/dist/redux/registration/registration.selectors";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  IContract,
  EInsuranceExternalStatus,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  isActiveContract,
  getIsShowExitChecklist,
} from "@rentiohq/shared-frontend/dist/utils/contract.utils";
import {
  memberHasOneOfRoles,
  memberHasRole,
} from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import {
  Card,
  ESpacings,
  RentioInternalRenderer,
} from "@rentiohq/web-shared/dist/components";
import { requestInsuranceValidationErrors } from "forms/requestInsurance/schema.requestInsurance.utils";
import { compact } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { ConfirmLegalActivationModal } from "scenes/RentioLegal/components/ConfirmLegalActivationModal";
import { useIsRentioLegalActivated } from "scenes/RentioLegal/hooks/useIsRentioLegalActivated";
import { NumberParam, StringParam } from "serialize-query-params";
import { IndexContractModal } from "../../../../../../components/IndexContractModalV2";
import { LocationDescriptionModal } from "../../../../../../components/LocationDescriptionModal";
import RequestInsuranceModal from "../../../../../../components/RequestInsuranceModal";
import RequestMoveServiceModal from "../../../../../../components/RequestMoveServiceModal";
import { RequestRentioMoveServiceModal } from "../../../../../../components/RequestRentioMoveServiceModal";
import TemplateDocumentContractCreateModal from "../../../../../../components/TemplateDocumentContractCreateModal";
import { AdministrationItems } from "./components/AdministrationItems";
import {
  useGetAdministrationEntryItems,
  useGetAdministrationExitItems,
} from "./components/AdministrationItems/useGetAdministrationItems";
import { ChecklistItems } from "./components/ChecklistItems";
import { useGetEntryChecklistItems } from "./components/ChecklistItems/useGetEntryChecklistItems";
import { useGetExitChecklistItems } from "./components/ChecklistItems/useGetExitChecklistItems";
import { ContractMetaData } from "./components/ContractMetaData";
import { IndexationCard } from "./components/IndexationCard";
import { LinkedProperties } from "./components/LinkedProperties";
import { RentioLegalCard } from "./components/RentioLegalCard";

interface IProps {
  contract: IContract;
  property: IProperty;
  showContractType?: boolean;
  showChecklistInitialState?: boolean;
  onCancelContract?: () => void;
}

export const ContractCard = (props: IProps) => {
  const {
    contract,
    property,
    showContractType = true,
    showChecklistInitialState = true,
    onCancelContract,
  } = props;

  const dispatch = useDispatch();
  // const navigate = useNavigate();

  const contractIsActive = isActiveContract(contract);
  const showExitChecklist = getIsShowExitChecklist(contract);

  const hasRentioLegal = useIsRentioLegalActivated();

  const [showEntryChecklistItems, setShowEntryChecklistItems] = React.useState(
    showChecklistInitialState,
  );
  const [showExitChecklistItems, setShowExitChecklistItems] =
    React.useState(false);

  const [queryParams, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    templateDocumentId: StringParam,
    documentPackageId: StringParam,
    rentDepositId: NumberParam,
    insuranceContractId: StringParam,
    requestInsuranceContractId: StringParam,
    moveRequestId: NumberParam,
    registrationId: NumberParam,
  });

  const registration = useSelector((state: IRootStore) =>
    registrationSelectors.registration(state, contract?.rentRegistrationId),
  );
  const isFetchingRegistration = useSelector((state: IRootStore) =>
    registrationSelectors.isFetching(state, contract?.rentRegistrationId),
  );

  // Local state
  const [showIndexContractModal, setShowIndexContractModal] =
    React.useState(false);
  const [
    showTemplateDocumentContractCreate,
    setShowTemplateDocumentContractCreate,
  ] = React.useState(false);
  const [showLocationDescriptionModal, setShowLocationDescriptionModal] =
    React.useState(false);
  const [showRequestInsuranceModal, setShowRequestInsuranceModal] =
    React.useState(false);
  const [didShowAutomaticInsuranceModal, setDidShowAutomaticInsuranceModal] =
    React.useState(false);
  const [showRequestMoveService, setShowRequestMoveServiceModal] =
    React.useState(false);
  const [showRequestRentioMoveService, setShowRequestRentioMoveService] =
    React.useState(false);
  const [contractToEnableRentioLegalFor, setContractToEnableRentioLegalFor] =
    React.useState<IContract>();

  // Redux selectors
  const { isBroker, broker } = authHooks.useSelf();
  const hasInsuranceForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Insurance,
  );

  const entryChecklistItems = useGetEntryChecklistItems({
    contract,
    property,
    registration,

    // Callbacks
    onIndexContract: () => {
      setShowIndexContractModal(true);
    },
    onCreateContractFromTemplateDocument: () => {
      setShowTemplateDocumentContractCreate(true);
    },
    onShowDocument: documentId => {
      setQueryParamValue({
        documentId,
      });
    },
    onShowTemplateDocument: templateDocumentId => {
      setQueryParamValue({
        templateDocumentId,
      });
    },
    onShowDocumentPackage: documentPackageId => {
      setQueryParamValue({
        documentPackageId,
      });
    },
    onShowRentDeposit: rentDepositId => {
      setQueryParamValue({
        rentDepositId,
      });
    },
    onOrderPropertyInspectionReport: () => {
      setShowLocationDescriptionModal(true);
    },
    onRequestInsurance: () => {
      setShowRequestInsuranceModal(true);
    },
    onShowInsurance: (insuranceContractId: string) => {
      setQueryParamValue({
        insuranceContractId,
      });
    },
    onCreateMoveRequest: () => {
      setShowRequestMoveServiceModal(true);
    },
    onCreateRentioMoveRequest: () => {
      setShowRequestRentioMoveService(true);
    },
    onShowMoveRequest: (moveRequestId: number) => {
      setQueryParamValue({ moveRequestId });
    },
    onShowRentRegistration: (registrationId: number) => {
      setQueryParamValue({
        registrationId,
      });
    },

    // Config
    shouldFetchData: showEntryChecklistItems,
  });

  const exitChecklistItems = useGetExitChecklistItems({
    contract,
    property,
    registration,

    // Callbacks
    onIndexContract: () => {
      setShowIndexContractModal(true);
    },
    onShowRentDeposit: rentDepositId => {
      setQueryParamValue({
        rentDepositId,
      });
    },
    onOrderPropertyInspectionReport: () => {
      setShowLocationDescriptionModal(true);
    },
    onCreateRentioMoveRequest: () => {
      setShowRequestRentioMoveService(true);
    },
    onShowMoveRequest: (moveRequestId: number) => {
      setQueryParamValue({ moveRequestId });
    },
    // Config
    shouldFetchData: !!showExitChecklist,
  });

  const entryItems = useGetAdministrationEntryItems({
    contract,
    property,
    checklistItems: entryChecklistItems,
    shouldFetchData: showEntryChecklistItems,
  });

  const exitItems = useGetAdministrationExitItems({
    contract,
    property,
    checklistItems: exitChecklistItems,
    shouldFetchData: showExitChecklistItems,
  });

  const hasPropertyManagement = property.members.some(member => {
    const isLoggedInBroker =
      isBroker && member.account.id === broker?.accountId;
    const hasMemberRole = memberHasRole(member, EPropertyMemberTypes.Broker);
    const hasManagerRole = memberHasOneOfRoles(member, [
      EPropertyMemberTypes.IndexationManager,
      EPropertyMemberTypes.FinancialManager,
      EPropertyMemberTypes.TechnicalManager,
    ]);

    return isLoggedInBroker && hasMemberRole && hasManagerRole;
  });

  React.useEffect(() => {
    if (
      contractIsActive &&
      hasPropertyManagement &&
      showChecklistInitialState &&
      !showExitChecklist
    ) {
      setShowEntryChecklistItems(false);
    }
  }, [contractIsActive]);

  React.useEffect(() => {
    if (showExitChecklist && showChecklistInitialState) {
      setShowEntryChecklistItems(false);
      setShowExitChecklistItems(true);
    }
  }, [showExitChecklist]);

  React.useEffect(() => {
    if (isFetchingRegistration || !contract?.rentRegistrationId) {
      return;
    }

    dispatch(
      registrationActions.getRegistration.actions.start({
        id: contract?.rentRegistrationId,
      }),
    );
  }, [contract?.rentRegistrationId]);

  // Automatically show request insurance modal after opening rent deposit
  React.useEffect(() => {
    if (isBroker && !hasInsuranceForBroker) {
      return;
    }

    if (didShowAutomaticInsuranceModal) {
      return;
    }

    if (!queryParams.requestInsuranceContractId) {
      return;
    }

    if (contract.id !== queryParams.requestInsuranceContractId) {
      return;
    }

    if (contract.insuranceExternalStatus !== EInsuranceExternalStatus.None) {
      return;
    }

    const validationErrors = requestInsuranceValidationErrors({
      property,
      contract,
    });

    if (validationErrors.length > 0) {
      return;
    }

    setShowRequestInsuranceModal(true);
    setDidShowAutomaticInsuranceModal(true);
  }, [contract, hasInsuranceForBroker, queryParams.requestInsuranceContractId]);

  // Event handlers
  const handleIndexModalClose = (documentId?: string) => {
    setShowIndexContractModal(false);
    setQueryParamValue({
      documentId,
    });
  };

  const handleLocationDescriptionModalClose = () => {
    setShowLocationDescriptionModal(false);
  };

  const handleClickRequestInsuranceModalClose = () => {
    setShowRequestInsuranceModal(false);
    setQueryParamValue({
      requestInsuranceContractId: undefined,
    });
  };

  const handleClickRequestMoveServiceModalClose = () => {
    setShowRequestMoveServiceModal(false);
  };

  const handleClickRequestRentioMoveServiceModalClose = () => {
    setShowRequestRentioMoveService(false);
  };

  const handleActivateRentioLegal = async (contract: IContract) => {
    if (!hasRentioLegal) {
      return;
    }

    setContractToEnableRentioLegalFor(contract);
  };

  const { ibanisationStatus, linkedProperties = [] } = contract;
  const indexationItem = entryChecklistItems["index"];

  // Render
  return (
    <>
      <Card key={contract.id} hasSections={true}>
        <Card.Section>
          <ContractMetaData
            contract={contract}
            property={property}
            showContractType={showContractType}
            onCancelContract={onCancelContract}
          />
        </Card.Section>

        {linkedProperties.length > 0 && (
          <Card.Section>
            <LinkedProperties contract={contract} />
          </Card.Section>
        )}

        {entryItems?.length && (
          <Card.Section
            boxProps={{
              px: ESpacings.loose,
              py: ESpacings.base,
            }}
          >
            <AdministrationItems
              items={entryItems}
              showCircles={showChecklistInitialState && !showExitChecklist}
              showChecklistItems={showEntryChecklistItems}
              setShowChecklistItems={setShowEntryChecklistItems}
              checklistItems={compact(Object.values(entryChecklistItems))}
            />
          </Card.Section>
        )}

        <RentioInternalRenderer
          items={{
            ibanisationStatus,
          }}
        />

        {showEntryChecklistItems && (
          <Card.Section boxProps={{ p: ESpacings.none }}>
            <ChecklistItems
              checklistItems={compact(Object.values(entryChecklistItems))}
              property={property}
              contract={contract}
            />
          </Card.Section>
        )}

        {exitItems?.length && exitChecklistItems && (
          <Card.Section
            boxProps={{
              px: ESpacings.loose,
              py: ESpacings.base,
            }}
          >
            <AdministrationItems
              items={exitItems}
              showCircles={showChecklistInitialState}
              showExitChecklist={showExitChecklist}
              showChecklistItems={showExitChecklistItems}
              setShowChecklistItems={setShowExitChecklistItems}
              checklistItems={compact(Object.values(exitChecklistItems))}
            />
          </Card.Section>
        )}

        {showExitChecklistItems && exitChecklistItems && (
          <Card.Section boxProps={{ p: ESpacings.none }}>
            <ChecklistItems
              checklistItems={compact(Object.values(exitChecklistItems))}
              property={property}
              contract={contract}
            />
          </Card.Section>
        )}

        {indexationItem && <IndexationCard indexationItem={indexationItem} />}

        {showIndexContractModal && (
          <IndexContractModal
            contractId={contract.id}
            onClose={handleIndexModalClose}
          />
        )}

        <RentioLegalCard
          contract={contract}
          onActivateRentioLegal={handleActivateRentioLegal}
        />
      </Card>

      {showTemplateDocumentContractCreate && (
        <TemplateDocumentContractCreateModal
          property={property}
          contract={contract}
          onClose={() => {
            setShowTemplateDocumentContractCreate(false);
          }}
          type={"contract"}
        />
      )}

      {showLocationDescriptionModal && (
        <LocationDescriptionModal
          property={property}
          contract={contract}
          onClose={handleLocationDescriptionModalClose}
        />
      )}

      {showRequestInsuranceModal && (
        <RequestInsuranceModal
          property={property}
          contract={contract}
          onClose={handleClickRequestInsuranceModalClose}
        />
      )}

      {showRequestMoveService && (
        <RequestMoveServiceModal
          property={property}
          contract={contract}
          onClose={handleClickRequestMoveServiceModalClose}
        />
      )}

      {showRequestRentioMoveService && (
        <RequestRentioMoveServiceModal
          property={property}
          contract={contract}
          onClose={handleClickRequestRentioMoveServiceModalClose}
          energyTransferByMexp={false}
        />
      )}

      {contractToEnableRentioLegalFor && (
        <ConfirmLegalActivationModal
          contract={contractToEnableRentioLegalFor}
          onSuccess={() => setContractToEnableRentioLegalFor(undefined)}
          onClose={() => setContractToEnableRentioLegalFor(undefined)}
        />
      )}
    </>
  );
};
